import React, { useEffect, useState } from 'react'
import axios from 'axios'
import SEO from '../components/SEO'
import LoadingSpinner from '../components/LoadingSpinner'
import defaultServices from '../data/defaultServices'

const servicesHeroImage = 'https://res.cloudinary.com/dmiicgsbq/image/upload/v1776967330/uxmbkii0eypf1mhcegvm.jpg'

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  headers: { 'Content-Type': 'application/json' }
})

const ServicesPage = () => {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)

  const mergeServices = (apiServices = []) => {
    const activeServices = apiServices.filter((service) => service.isActive)
    const normalizedActiveTitles = new Set(
      activeServices.map((service) => service.title.trim().toLowerCase())
    )

    const missingDefaults = defaultServices.filter(
      (service) => !normalizedActiveTitles.has(service.title.trim().toLowerCase())
    )

    return [...activeServices, ...missingDefaults]
  }

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true)
        const response = await apiClient.get('/api/services')
        setServices(mergeServices(response.data))
      } catch (error) {
        console.error('Error fetching services:', error)
        // Use default services if API fails
        setServices(defaultServices)
      } finally {
        setLoading(false)
      }
    }

    fetchServices()

    // Auto-refresh when page regains focus
    const handleFocus = () => {
      fetchServices()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  if (loading) return <LoadingSpinner />

  return (
    <div className="container py-5 page-grid-gap themed-page themed-services-page">
      <SEO 
        title="Services" 
        description="Explore our medical services including General Checkup, Cardiology, Dental Care, Pediatric Care, and Orthopedic Consultation."
        keywords="medical services, general checkup, cardiology, dental care, pediatrics, orthopedics, healthcare services"
        url="https://harmonymedical.com/services"
      />
      <section className="page-hero themed-page-hero themed-page-hero-compact split-page-hero split-page-hero-services">
        <div className="split-page-hero-layout">
          <div className="split-page-hero-copy">
            <span className="hero-kicker">Clinical Support</span>
            <h1 className="mb-3">Our Services</h1>
            <p className="text-muted mb-0">
              Explore the consultations, diagnostics, and follow-up care available across our clinic, then book the service that matches your health concern.
            </p>
          </div>
          <div className="split-page-hero-visual" aria-hidden="true">
            <div className="split-page-hero-image-shell">
              <img
                src={servicesHeroImage}
                alt="Modern clinical consultation"
                className="split-page-hero-image"
              />
            </div>
          </div>
        </div>
      </section>
      {services.length === 0 ? (
        <div className="alert alert-info">No services available at the moment.</div>
      ) : (
        <section className="editorial-section editorial-band">
          <div className="split-info-band">
            <div className="editorial-heading-block split-info-band-copy">
              <span className="editorial-kicker">Available Care</span>
              <h2 className="editorial-title">Find the consultation, diagnostics, or follow-up support that fits your needs.</h2>
              <p className="editorial-lead">
                Browse each service to understand what it covers, compare care options more easily, and book the appointment that matches your health concern.
              </p>
            </div>
            <div className="split-info-band-aside">
              <div className="split-info-band-card split-info-band-card-services">
                <div className="split-info-band-chip">Patient Journey</div>
                <div className="split-service-story">
                  <div className="split-service-story-step">
                    <strong>1. Consultation</strong>
                    <span>Start with the right specialty and an easy booking path.</span>
                  </div>
                  <div className="split-service-story-step">
                    <strong>2. Diagnostics</strong>
                    <span>Move into tests, review, and doctor-guided treatment planning.</span>
                  </div>
                  <div className="split-service-story-step">
                    <strong>3. Follow-up Care</strong>
                    <span>Stay supported with recovery guidance and long-term monitoring.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row g-4">
            {services.map((service) => (
              <div key={service._id} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm border-0 themed-service-card">
                  <div className="card-body d-flex flex-column">
                    <span className="themed-service-icon" aria-hidden="true"></span>
                    <h5 className="card-title">{service.title}</h5>
                    <p className="card-text text-muted mb-2">{service.description}</p>
                    <p className="fw-bold mb-3">Price: ${service.price}</p>
                    <a href={`/appointment?service=${encodeURIComponent(service.title)}`} className="btn btn-primary mt-auto">
                      Book Appointment
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default ServicesPage
